/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import {
  Tooltip,
  Row,
  Col,
  Modal,
  Form,
  Button,
  Input,
  message,
} from "antd";
import CreativeImage from "./CreativeImage";
import { isEmpty } from "lodash";
import PaginationTable from "../../../shared/PaginationTable";
import moment from "moment";
// Global Styling
import {
  TableColumnTitle,
  PrimaryTableRowText,
  TooltipText,
} from "../../../shared/globalStyling/styledText";
import { EditOutlined, LoadingOutlined, SaveOutlined } from "@ant-design/icons";
import { useLibraryCreativesContext } from "../../../../core/components/creatives/library/useLibraryCreativesContext";
import { useHistory, useLocation } from "react-router-dom";
import { performCreativeSearch } from "../../Creatives/utils/mediaUtils";
const CreativeTable = ({
  orgMedias,
  ids,
  setIds,
  setCreativeKeys,
  searchText,
  currentPage: propCurrentPage,
  onPageChange,
  activeTab,
  activeSubTab,
  clearSelectionTrigger,
}) => {
  const { updateOrgMedia } = useLibraryCreativesContext() || {};
  const history = useHistory();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(propCurrentPage || 1);
  const [medias, setMedias] = useState([]);
  const [localSelectedKeys, setLocalSelectedKeys] = useState([]);
  const [editMedia, setEditMedia] = useState(false);
  const [editMediaSubmit, setEditMediaSubmit] = useState(false);
  const [creativeID, setCreativeID] = useState('');
  const [name, setName] = useState('');
  const [cloneName, setCloneName] = useState('');
  const [url, setUrl] = useState('');
  const [form] = Form.useForm();
  const [pageSize, setPageSize] = useState(10);
  const editingMediaRef = useRef(null);


  useEffect(() => {
    let isMounted = true;

    const processImagesWithMountCheck = (orgMedias) => {
      const medias = orgMedias.map((media) => {
        const image = (
          <div style={{ maxHeight: 50, maxWidth: 100 }}>
            <CreativeImage
              src={media.url}
              alt="Creative Image"
              style={{ maxHeight: 50, maxWidth: 100 }}
              fallbackText="Creative Image"
            />
          </div>
        );
        media.image = image;
        return media;
      });
      if (isMounted) {
        setMedias(medias);
      }
    };

    if (!isEmpty(orgMedias)) {
      processImagesWithMountCheck(orgMedias);
    } else if (isMounted) {
      setMedias(orgMedias);
    }

    return () => {
      isMounted = false;
    };
  }, [orgMedias]);

  // Sync currentPage with prop
  useEffect(() => {
    if (propCurrentPage && propCurrentPage !== currentPage) {
      setCurrentPage(propCurrentPage);
    }
  }, [propCurrentPage]);

  const extraColumn = {
    title: "",
    dataIndex: "image",
    key: "image",
    width: "60px",
    maxheight: "45px",
    align: "left",
  };

  let columns = [
    {
      title: <TableColumnTitle text={"Display Ad Unit Name"} />,
      dataIndex: "name",
      key: "name",
      width: "40%",
      align: "left",
      padding: 0,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <Row
          type="flex"
          justify="start"
          align="middle"
          style={{
            width: "100%",
          }}
          onClick={() => {
            // Clean URL navigation (consistent with ad groups)
            history.push(`/creatives/display/units/${record.id}`);
          }}
        >
          <Col span={24}>
            {text.length > 45 ? (
              <Tooltip title={<TooltipText text={text} />}>
                <span>
                  <PrimaryTableRowText text={text.slice(0, 46).concat("...")} />
                </span>
              </Tooltip>
            ) : (
              <PrimaryTableRowText text={text} />
            )}
          </Col>
        </Row>
      ),
    },
    {
      title: <TableColumnTitle text={"Aspect"} />,
      dataIndex: "aspect",
      key: "aspect",
      width: "10%",
      align: "left",
      sorter: (a, b) => a.width - b.width || a.height - b.height,
      render: (_, record) => (
        <div>
          <PrimaryTableRowText text={`${record.width}x${record.height}`} />
        </div>
      ),
    },
    {
      title: <TableColumnTitle text={"File Type"} />,
      dataIndex: "fileType",
      key: "fileType",
      width: "10%",
      align: "left",
      sorter: (a, b) => a.fileType.localeCompare(b.fileType),
      render: (fileType) => <PrimaryTableRowText text={fileType} />,
    },
    {
      title: <TableColumnTitle text={'Date Uploaded'} />,
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: '15%',
      align: 'left',
      sorter: (a, b) => {
        const dateA = moment(a.createdAt);
        const dateB = moment(b.createdAt);
        return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
      },
      render: (createdAt) => {
        return <PrimaryTableRowText text={moment(createdAt).format('MM/DD/YYYY hh:mm A')} />;
      },
    },
    {
      title: "",
      key: "edit",
      width: "10%",
      align: "right",
      render: creatives => (
        <EditOutlined
          style={{ fontSize: '1.25rem' }}
          onClick={() => {
            editOrgMedia(creatives);
          }}
        />
      ),
    },
  ];
  if (orgMedias && !isEmpty(orgMedias)) {
    columns.unshift(extraColumn);
  }

  if (!updateOrgMedia) {
    // Filter edit column if updateOrgMedia is not passed
    columns = columns.filter(column => column.key !== "edit");
  }

  const validateEditMediaName = (_, value) => {
    if (!value) return Promise.resolve();

    // Use ref to get the media being edited (avoids state timing issues)
    const editingMedia = editingMediaRef.current;
    if (!editingMedia) return Promise.resolve();

    const lowerValue = value.toLowerCase().trim();
    const currentName = editingMedia.name.toLowerCase().trim();

    // Allow if it's the same as current (no change)
    if (lowerValue === currentName) return Promise.resolve();

    // Check for duplicates against all existing media (case-insensitive)
    // Handle both array and { medias: [...] } formats
    const currentMediaId = editingMedia.id;
    const mediaList = Array.isArray(orgMedias) ? orgMedias : (orgMedias?.medias || []);

    const isDuplicate = mediaList.some(
      media => media.name?.toLowerCase().trim() === lowerValue && media.id !== currentMediaId
    );

    if (isDuplicate) {
      return Promise.reject(new Error("Creative name already exists"));
    }

    return Promise.resolve();
  };

  const editOrgMedia = async data => {
    // Store in ref first so validator can access it immediately
    editingMediaRef.current = data;

    // Set state for UI updates
    setCreativeID(data.id);
    setName(data.name);
    setCloneName(data.name);
    setUrl(data.url);

    // Reset and set form values
    form.resetFields();
    form.setFields([
      {
        name: 'name',
        value: data.name,
        touched: false,
        errors: [],  // Explicitly clear any validation errors
      },
    ]);

    setEditMedia(true);
  };

  const handleSubmit = async () => {
    if (name === cloneName) return;

    try {
      setEditMediaSubmit(true);

      message.loading('Action in progress...', 0);

      const CapitalizeCreativeName = name.charAt(0).toUpperCase() + name.slice(1);

      await updateOrgMedia({
        variables: {
          creativeID,
          newUrl: url,
          newName: CapitalizeCreativeName,
        },
      });

      form.resetFields();
      setEditMediaSubmit(false);
      setCreativeID('');
      setUrl('');
      setName('');
      setEditMedia(false);
      message.destroy();
      message.success(`Successfully updated the creative name`);
    } catch (err) {
      message.destroy();
      message.error(`Error updating creative name`);
    }
  };

  const rowSelection = {
    selectedRowKeys: localSelectedKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      const creativeKeys = selectedRows.map(row => {
        return row.key;
      });

      setLocalSelectedKeys(selectedRowKeys);
      setIds(selectedRowKeys);
      setCreativeKeys(creativeKeys);
    },
    /* UNUSED: Table selection options - removed from UI design
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
    ],
    */
  };

  useEffect(() => {
    // Use standardized search function for media items
    const searchFiltered = performCreativeSearch(orgMedias, searchText, 'media');
    setMedias(searchFiltered);
  }, [searchText, orgMedias]);

  // Sync local selection with ids prop (for initialization and updates)
  useEffect(() => {
    if (Array.isArray(ids)) {
      setLocalSelectedKeys(ids);
    }
  }, [ids]);

  // Clear local selection when clearSelectionTrigger changes
  useEffect(() => {
    if (clearSelectionTrigger > 0) {
      setLocalSelectedKeys([]);
    }
  }, [clearSelectionTrigger]);

  return (
    <>
      <Modal
        title="Edit Media"
        open={editMedia}
        centered
        footer={null}
        onCancel={() => {
          setEditMedia(false);
          form.resetFields();
        }}
      >
        <Form
          layout="vertical"
          onFinish={handleSubmit}
          form={form}
        >
          <Row type="flex" justify="space-around">
            <Col span={24}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <img src={url} alt={name} width="125" height="100" />
              </div>
            </Col>
          </Row>
          <Row type="flex" justify="space-around">
            <Col span={24}>
              <Form.Item
                label="Name (Max 255 characters)"
                name="name"
                validateTrigger="onBlur"
                rules={[
                  { required: true, message: "Please enter a creative name" },
                  { max: 255, message: "Creative name must not exceed 255 characters" },
                  {
                    validator: validateEditMediaName,
                  },
                ]}
              >
                <Input
                  placeholder="Name of Creative"
                  maxLength={255}
                  showCount
                  onChange={(e) => {
                    const CapitalizeCreativeName =
                      e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1);
                    setName(CapitalizeCreativeName);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row type="flex" justify="space-around" align="middle">
            <Col span={24}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  disabled={name === cloneName}
                  icon={editMediaSubmit ? <LoadingOutlined /> : <SaveOutlined />}
                  htmlType="submit"
                  type="primary"
                >
                  Update
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>
      <PaginationTable
        /* UNUSED: footer calculation - removed from table design */
        // footer={medias && medias.slice(currentPage * 10 - 10, currentPage * 10)}
        bordered={false}
        columns={columns}
        data={medias}
        scroll={{ x: 1250, y: false }}
        paginationSize={'default'}
        size={'default'}
        total={medias && medias.length}
        current={currentPage}
        pageSize={pageSize}
        rowKey="id"
        rowSelection={rowSelection}
        onChange={paginationObject => {
          setPageSize(paginationObject.pageSize);
          setCurrentPage(paginationObject.current);
          if (onPageChange) {
            onPageChange(paginationObject.current);
          }
        }}
      />
    </>
  );
};

export default CreativeTable;
